import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PhygitalIcon from "@/components/PhygitalIcon";
import EarlyAccessModal from "@/components/EarlyAccessModal";
import SharedFooter from "@/components/SharedFooter";
import AllCategoriesSection from "@/components/AllCategoriesSection";
import { EditableText, EditableSection, useAdmin } from "@/components/AdminContentEditor";
import {
  Sparkles,
  ArrowRight,
  Eye,
  Heart,
  Star,
  Users,
  Calendar,
  CheckCircle2,
  Clock,
  MessageCircle,
  Image,
  Camera,
  RefreshCw,
  Bell,
  Palette,
  Scissors,
  AlertCircle
} from "lucide-react";

const LashSalesPage = () => {
  const [showEarlyAccessModal, setShowEarlyAccessModal] = useState(false);
  const [demoTab, setDemoTab] = useState("client");
  const [flowStep, setFlowStep] = useState(1);
  const { editMode } = useAdmin();

  useEffect(() => {
    document.title = "phygital - For Lash Artists";
  }, []);

  // Lash services
  const lashServices = [
    { name: "Classic Full Set", duration: "2 hours", price: "$150+", desc: "Natural, everyday look" },
    { name: "Volume Full Set", duration: "2.5 hours", price: "$200+", desc: "Dramatic, full lashes" },
    { name: "Mega Volume Set", duration: "3 hours", price: "$275+", desc: "Maximum impact" },
    { name: "Lash Fill", duration: "1 hour", price: "$75+", desc: "2-3 week maintenance" },
  ];

  // Mock client preferences
  const mockClientPrefs = {
    name: "Client",
    defaults: {
      lashStyle: "Cat eye, natural volume",
      curlType: "C curl",
      length: "10-14mm, longest at outer corners",
      allergies: "None",
      sensitivity: "Sensitive eyes - fragrance-free glue"
    },
    thisAppt: {
      service: "Volume Fill",
      look: "More dramatic for wedding weekend",
      inspo: ["Reference 1", "Reference 2"],
      notes: "Attending sister's wedding Saturday"
    },
    vibe: "Relaxed, okay to nap",
    timing: "Can run over if needed"
  };

  // Booking flow steps
  const flowSteps = [
    { num: 1, title: "Book Online", desc: "Client selects service & time" },
    { num: 2, title: "Preference Prompt", desc: "Share lash style for this appointment" },
    { num: 3, title: "Artist Reviews", desc: "See style, inspo, and prepare" },
    { num: 4, title: "Day Of", desc: "Everything ready, client relaxes" }
  ];

  const lashFeatures = [
    {
      icon: Eye,
      title: "Lash style profiles",
      description: "Curl type, length mapping, volume preferences - saved for every visit"
    },
    {
      icon: Heart,
      title: "Sensitivity tracking",
      description: "Allergies, sensitivities, preferred adhesives - never forgotten"
    },
    {
      icon: Image,
      title: "Visual inspiration",
      description: "Clients share inspo photos before appointments for perfect execution"
    },
    {
      icon: MessageCircle,
      title: "Appointment notes",
      description: "Special events, timing preferences, communication style"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" variant="light" />
            <span className="font-semibold text-xl">phygital</span>
          </Link>
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">
              <Eye className="w-3 h-3 mr-1" />
              Lash
              <Badge variant="outline" className="ml-2 text-xs bg-purple-50 text-purple-600 border-purple-200">
                Demo
              </Badge>
            </Badge>
            <Button
              onClick={() => setShowEarlyAccessModal(true)}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="lash-early-access-btn"
            >
              Get Early Access
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <EditableSection sectionId="lash-hero" label="Lash Hero" className={editMode ? "relative overflow-hidden py-20 px-4" : "relative overflow-hidden py-20 px-4"}>
        <div className="absolute inset-0 bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-64 h-64 bg-violet-200 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-purple-200 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-4xl mx-auto text-center">
          <Badge className="mb-6 bg-violet-100 text-violet-700 border border-violet-200">
            <Eye className="w-3 h-3 mr-1" />
            <EditableText sectionId="lash-hero" field="badge" defaultValue="Lash Pilot by Phygital" className="inline" />
          </Badge>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
            <EditableText sectionId="lash-hero" field="title_line1" defaultValue="Know Your Client's" className="inline" /><br />
            <span className="bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 bg-clip-text text-transparent">
              <EditableText sectionId="lash-hero" field="title_highlight" defaultValue="Lash Style" className="inline" />
            </span>{" "}
            <EditableText sectionId="lash-hero" field="title_line2" defaultValue="Before They Close Their Eyes" className="inline" />
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            <EditableText 
              sectionId="lash-hero" 
              field="subtitle" 
              defaultValue="Keep using your booking system. Phygital adds a preference layer so clients share their curl, length, and style before every appointment."
              multiline
            />
          </p>
          
          <div className="flex justify-center gap-4 flex-wrap">
            <Button
              onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              className="bg-violet-600 hover:bg-violet-700 text-white h-12 px-8 rounded-full"
              data-testid="see-demo-btn"
            >
              See How It Works
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              onClick={() => setShowEarlyAccessModal(true)}
              size="lg"
              variant="outline"
              className="border-violet-300 text-violet-700 hover:bg-violet-50 h-12 px-8 rounded-full"
            >
              Get Early Access
            </Button>
          </div>
        </div>
      </EditableSection>

      {/* Services Preview */}
      <section className="py-12 px-4 bg-white border-b">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-semibold text-center mb-6 text-gray-900">
            Works with your existing services
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {lashServices.map((service, idx) => (
              <Card key={idx} className="border-violet-100">
                <CardContent className="p-4 text-center">
                  <p className="font-medium text-sm">{service.name}</p>
                  <p className="text-xs text-gray-500">{service.duration}</p>
                  <p className="text-sm font-semibold text-violet-600 mt-1">{service.price}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo" className="py-16 px-4 bg-gradient-to-b from-white to-violet-50/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              See What Changes With Phygital
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Switch between views to see how preferences flow from client to artist.
            </p>
          </div>

          <Tabs value={demoTab} onValueChange={setDemoTab} className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
              <TabsTrigger value="client" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white">
                <Users className="w-4 h-4 mr-2" />
                Client View
              </TabsTrigger>
              <TabsTrigger value="artist" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white">
                <Eye className="w-4 h-4 mr-2" />
                Artist View
              </TabsTrigger>
            </TabsList>

            <TabsContent value="client" className="mt-0">
              <Card className="max-w-lg mx-auto border-violet-200 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center text-white font-semibold">
                      J
                    </div>
                    <div>
                      <p className="font-semibold">Jamie's Lash Profile</p>
                      <p className="text-sm text-gray-500">Saved preferences + this appointment</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-violet-50 rounded-lg p-4">
                      <p className="text-xs font-medium text-violet-700 mb-2">MY DEFAULTS</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Style</span>
                          <span className="font-medium">{mockClientPrefs.defaults.lashStyle}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Curl</span>
                          <span className="font-medium">{mockClientPrefs.defaults.curlType}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Length</span>
                          <span className="font-medium">{mockClientPrefs.defaults.length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Sensitivity</span>
                          <span className="font-medium text-amber-600">{mockClientPrefs.defaults.sensitivity}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-4">
                      <p className="text-xs font-medium text-purple-700 mb-2">THIS APPOINTMENT</p>
                      <div className="space-y-2 text-sm">
                        <p><span className="text-gray-600">Service:</span> <span className="font-medium">{mockClientPrefs.thisAppt.service}</span></p>
                        <p><span className="text-gray-600">Look:</span> <span className="font-medium">{mockClientPrefs.thisAppt.look}</span></p>
                        <p><span className="text-gray-600">Note:</span> <span className="font-medium">{mockClientPrefs.thisAppt.notes}</span></p>
                      </div>
                    </div>

                    <div className="flex gap-2 text-xs">
                      <Badge variant="outline" className="bg-violet-50">Vibe: {mockClientPrefs.vibe}</Badge>
                      <Badge variant="outline" className="bg-violet-50">Timing: {mockClientPrefs.timing}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="artist" className="mt-0">
              <Card className="max-w-lg mx-auto border-violet-200 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <p className="font-semibold text-lg">Upcoming: Jamie</p>
                      <p className="text-sm text-gray-500">Volume Fill - 2:00 PM</p>
                    </div>
                    <Badge className="bg-violet-100 text-violet-700">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Preferences Ready
                    </Badge>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                        <p className="text-sm font-medium text-amber-700">Important Note</p>
                      </div>
                      <p className="text-sm text-amber-800">{mockClientPrefs.defaults.sensitivity}</p>
                    </div>

                    <div className="bg-violet-50 rounded-lg p-4">
                      <p className="text-xs font-medium text-violet-700 mb-2">LASH SPECS</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-gray-500">Style</p>
                          <p className="font-medium">{mockClientPrefs.defaults.lashStyle}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Curl</p>
                          <p className="font-medium">{mockClientPrefs.defaults.curlType}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-gray-500">Length Mapping</p>
                          <p className="font-medium">{mockClientPrefs.defaults.length}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-4">
                      <p className="text-xs font-medium text-purple-700 mb-2">TODAY'S REQUEST</p>
                      <p className="text-sm font-medium">{mockClientPrefs.thisAppt.look}</p>
                      <p className="text-xs text-gray-500 mt-1">{mockClientPrefs.thisAppt.notes}</p>
                    </div>

                    <div className="flex gap-2 text-xs">
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        {mockClientPrefs.vibe}
                      </Badge>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        {mockClientPrefs.timing}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Flow Steps */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">
            How It Works
          </h2>
          <p className="text-gray-600 text-center mb-10 max-w-xl mx-auto">
            Phygital layers onto your existing booking system. No changes needed.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {flowSteps.map((step, idx) => (
              <div key={idx} className="text-center">
                <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-3">
                  <span className="text-lg font-bold text-violet-600">{step.num}</span>
                </div>
                <h3 className="font-semibold text-sm mb-1">{step.title}</h3>
                <p className="text-xs text-gray-500">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Preview */}
      <section className="py-16 px-4 bg-gradient-to-b from-violet-50 to-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">
            Built for Lash Artists
          </h2>
          <p className="text-gray-600 text-center mb-10 max-w-xl mx-auto">
            Everything you need to deliver personalized lash experiences.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {lashFeatures.map((feature, idx) => (
              <Card key={idx} className="border-violet-100 hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center mb-4">
                    <feature.icon className="w-5 h-5 text-violet-600" />
                  </div>
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to transform your lash business?
          </h2>
          <p className="text-violet-100 text-lg mb-8">
            Join the pilot program and start receiving client preferences before every appointment.
          </p>
          <Button
            onClick={() => setShowEarlyAccessModal(true)}
            size="lg"
            className="bg-white text-violet-700 hover:bg-violet-50 h-12 px-8 rounded-full font-semibold"
            data-testid="cta-early-access-btn"
          >
            Get Early Access
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* All Categories */}
      <AllCategoriesSection currentCategory="lash" />

      {/* Footer */}
      <SharedFooter />

      {/* Early Access Modal */}
      <EarlyAccessModal
        open={showEarlyAccessModal}
        onOpenChange={setShowEarlyAccessModal}
        category="lashes"
      />
    </div>
  );
};

export default LashSalesPage;
